/**
 * Scale anchors (min / midpoint / max wording) for a single rating column.
 *
 * The three-band bar only shows how the sample split; it never says which end is
 * which. The card's left-hand blurb does ("1 means 'Very unlikely' and 5 means
 * 'Very likely'"), but that is a long way from the bar. These anchors let the
 * exhibit repeat the wording under the track it belongs to.
 *
 * Generic over any SurveyJS rating-ish column: labels are read from whatever the
 * column actually carries (`rateValues`, `choices`, or the endpoint-only
 * `minRateDescription` / `maxRateDescription`) and fall back to the bare number
 * when the scale is unlabeled. No question ids, no scale-specific special cases.
 */

export interface ScaleAnchors {
  minLabel: string
  midLabel: string
  maxLabel: string
  /** True when at least one anchor carries wording beyond its bare number. */
  hasWording: boolean
}

interface LabeledPoint {
  value: number
  text: string
}

function readLabeledPoints(source: unknown): Array<LabeledPoint> {
  if (!Array.isArray(source)) {
    return []
  }
  return source
    .map((entry: any) => {
      const rawValue = entry && typeof entry === 'object' ? (entry.value ?? entry.name) : entry
      const rawText = entry && typeof entry === 'object' ? (entry.text ?? entry.title) : entry
      const value = Number(rawValue)
      if (!Number.isFinite(value)) {
        return null
      }
      const text = String(rawText ?? rawValue ?? '').trim()
      if (!text) {
        return null
      }
      return { value, text }
    })
    .filter((point): point is LabeledPoint => point !== null)
}

/**
 * Wording for one scale point, or null when the column only knows it as a number.
 * `rateValues` wins over `choices` because SurveyJS treats it as the display list
 * when both are present.
 */
function labelForValue(column: unknown, value: number): string | null {
  if (!column || typeof column !== 'object') {
    return null
  }
  const record = column as Record<string, unknown>
  const sources = [readLabeledPoints(record.rateValues), readLabeledPoints(record.choices)]
  for (const points of sources) {
    const hit = points.find((point) => point.value === value)
    if (hit && hit.text !== String(value)) {
      return hit.text
    }
  }
  return null
}

function endpointDescription(column: unknown, key: 'minRateDescription' | 'maxRateDescription'): string | null {
  if (!column || typeof column !== 'object') {
    return null
  }
  const raw = (column as Record<string, unknown>)[key]
  const text = typeof raw === 'string' ? raw.trim() : ''
  return text || null
}

function formatMidpoint(mid: number): string {
  return `Midpoint (${mid.toFixed(1)})`
}

/**
 * Resolve the three anchors for `[min, max]`. The midpoint keeps its numeric
 * "Midpoint (3.0)" form unless the scale is fully labeled and the midpoint lands
 * on an actual labeled point (an even scale has no middle point to name).
 */
export function resolveScaleAnchors(column: unknown, min: number, max: number): ScaleAnchors {
  const mid = (min + max) / 2
  const minWording = labelForValue(column, min) ?? endpointDescription(column, 'minRateDescription')
  const maxWording = labelForValue(column, max) ?? endpointDescription(column, 'maxRateDescription')
  const midWording = Number.isInteger(mid) ? labelForValue(column, mid) : null

  return {
    minLabel: minWording ?? String(min),
    midLabel: midWording ?? formatMidpoint(mid),
    maxLabel: maxWording ?? String(max),
    hasWording: Boolean(minWording || maxWording || midWording),
  }
}

export function sameScaleAnchors(a: ScaleAnchors, b: ScaleAnchors): boolean {
  return a.minLabel === b.minLabel && a.midLabel === b.midLabel && a.maxLabel === b.maxLabel
}
